import React, { useState, useEffect, useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomerService from "../services/CustomerService";
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Button } from './common';
import log from "../services/logger";
import { ApplicationContext } from './ApplicationContext';
import {
  customerUpdateErrorMessage,
  identificationNumberChanged,
  loginChangeNotice,
  loginUsernameAfterSave,
  newLoginPasswordSchema,
  passwordFailureMessage,
  trimLoginFields,
} from './clientLoginAccess';

const EMPTY_FORM_VALUES = {
  customerCode: "",
  nameOfTheLegalEntity: "",
  city: "",
  address: "",
  pib: "",
  identificationNumber: "",
  contactPerson: "",
  contactNumber: "",
  email: "",
  isActive: 'true',
  comment: "",
  paymentCurrency: "",
  newLoginPassword: "",
};

const toFormValues = (customer) => ({
  ...EMPTY_FORM_VALUES,
  customerCode: customer.customerCode || "",
  nameOfTheLegalEntity: customer.nameOfTheLegalEntity || "",
  city: customer.city || "",
  address: customer.address || "",
  pib: customer.pib || "",
  identificationNumber: customer.identificationNumber || "",
  contactPerson: customer.contactPerson || "",
  contactNumber: customer.contactNumber || "",
  email: customer.email || "",
  isActive: customer.isActive ? 'true' : 'false',
  comment: customer.comment || "",
  paymentCurrency: customer.paymentCurrency || "",
});

// Resolves to the customer's login ({ username, customerLogin }) or null; never blocks the form.
const fetchLoginAccess = (clientID) =>
  CustomerService.getLoginAccess(clientID)
    .then(response => response.data)
    .catch(error => {
      log.warn(error);
      return null;
    });

const FormSection = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow-md p-6 mb-6">
    <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {children}
    </div>
  </div>
);

// Credentials notices must stay until the admin closes them; she passes them on to the shop.
const PERSISTENT_TOAST = { autoClose: false };

const EditClient = () => {
  const [loading, setLoading] = useState(true);
  const [loadedValues, setLoadedValues] = useState(EMPTY_FORM_VALUES);
  const [loginAccess, setLoginAccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const { id: clientID } = useParams();
  const navigate = useNavigate();
  // The login endpoints are admin-only; other roles never see or request the login.
  const { userRole } = useContext(ApplicationContext);
  const isAdmin = userRole === 'ROLE_ADMIN';

  // After a partial save the admin stays here, so later comparisons must start from what is stored now.
  const rememberSavedValues = (savedValues) => {
    if (loginAccess) {
      setLoginAccess({ ...loginAccess, username: loginUsernameAfterSave(loginAccess, loadedValues, savedValues) });
    }
    setLoadedValues({ ...savedValues, newLoginPassword: "" });
  };

  const formik = useFormik({
    // Filled by resetForm once the customer is loaded, in the same render that first shows the form.
    initialValues: EMPTY_FORM_VALUES,
    validationSchema: Yup.object().shape({
      nameOfTheLegalEntity: Yup.string().trim().required('Polje "Ime legalnog entiteta" je obavezno.'),
      city: Yup.string().required('Polje "Grad" je obavezno.'),
      address: Yup.string().required('Polje "Adresa" je obavezno.'),
      pib: Yup.string()
        .length(9, 'Polje "PIB" mora imati tačno 9 brojeva.')
        .matches(/^[0-9]+$/, 'Polje "PIB" mora sadržati samo brojeve.')
        .required('Polje "PIB" je obavezno.'),
      identificationNumber: Yup.string()
        .trim()
        .length(8, 'Polje "Matični broj" mora imati tačno 8 brojeva.')
        .matches(/^[0-9]+$/, 'Polje "Matični broj" mora sadržati samo brojeve.')
        .required('Polje "Matični broj" je obavezno.'),
      contactPerson: Yup.string().required('Polje "Kontakt osoba" je obavezno.'),
      contactNumber: Yup.string().required('Polje "Kontakt broj" je obavezno.'),
      email: Yup.string().trim().email('Nevažeća email adresa.').required('Polje "Email" je obavezno.'),
      paymentCurrency: Yup.string().required('Polje "Valuta plaćanja" je obavezno.'),
      newLoginPassword: newLoginPasswordSchema,
    }),
    // The login password never travels in the customer body; it has its own endpoint.
    onSubmit: async ({ newLoginPassword, ...editedValues }) => {
      const customerValues = trimLoginFields(editedValues);
      try {
        await CustomerService.update(clientID, customerValues);
      } catch (error) {
        log.error(error);
        toast.error(customerUpdateErrorMessage(error));
        return;
      }
      toast.success('Uspešno ažuriran klijent!');

      const passwordSet = newLoginPassword !== "";
      if (passwordSet) {
        try {
          await CustomerService.setLoginPassword(clientID, newLoginPassword);
        } catch (error) {
          log.error(error);
          toast.error(passwordFailureMessage({
            error,
            access: loginAccess,
            original: loadedValues,
            values: customerValues,
          }), PERSISTENT_TOAST);
          rememberSavedValues(customerValues);
          return;
        }
      }

      const notice = loginChangeNotice({
        access: loginAccess,
        original: loadedValues,
        values: customerValues,
        passwordSet,
      });
      if (notice) {
        toast.info(notice, PERSISTENT_TOAST);
      }
      navigate('/clients');
    },
  });

  const { resetForm } = formik;
  useEffect(() => {
    const fetchClient = async () => {
      setLoading(true);
      const loginAccessRequest = isAdmin ? fetchLoginAccess(clientID) : Promise.resolve(null);
      try {
        const response = await CustomerService.get(clientID);
        const values = toFormValues(response.data);
        setLoadedValues(values);
        resetForm({ values });
      } catch (error) {
        toast.error('Greška pri učitavanju podataka o klijentu');
        log.error(error);
      } finally {
        setLoginAccess(await loginAccessRequest);
        setLoading(false);
      }
    };

    fetchClient();
  }, [clientID, isAdmin, resetForm]);

  const showLoginAccess = isAdmin && loginAccess?.customerLogin === true;
  const passwordBecomesIdentificationNumber = showLoginAccess
    && formik.values.identificationNumber.trim() !== ""
    && identificationNumberChanged(loadedValues, formik.values)
    && formik.values.newLoginPassword === "";

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Izmena Klijenta</h2>
          <p className="mt-1 text-sm text-gray-600">
            Izmenite informacije o klijentu
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-6">
          <FormSection title="Osnovne Informacije">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Šifra Klijenta
              </label>
              <input
                type="text"
                name="customerCode"
                value={formik.values.customerCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.customerCode && formik.errors.customerCode && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.customerCode}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Naziv Pravnog Lica
              </label>
              <input
                type="text"
                name="nameOfTheLegalEntity"
                value={formik.values.nameOfTheLegalEntity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.nameOfTheLegalEntity && formik.errors.nameOfTheLegalEntity && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.nameOfTheLegalEntity}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PIB
              </label>
              <input
                type="text"
                name="pib"
                value={formik.values.pib}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.pib && formik.errors.pib && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.pib}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matični Broj
              </label>
              <input
                type="text"
                name="identificationNumber"
                value={formik.values.identificationNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.identificationNumber && formik.errors.identificationNumber && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.identificationNumber}</div>
              )}
              {passwordBecomesIdentificationNumber && (
                <div className="text-amber-600 text-xs mt-1">
                  Po čuvanju, lozinka za prijavu biće novi matični broj: {formik.values.identificationNumber.trim()}.
                </div>
              )}
            </div>
          </FormSection>

          {showLoginAccess && (
            <FormSection title="Pristup portalu">
              <div className="md:col-span-2">
                <p className="text-sm text-gray-700">
                  Korisničko ime: <span className="font-semibold">
                    {loginUsernameAfterSave(loginAccess, loadedValues, formik.values)}
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Početna lozinka je matični broj klijenta. Ako promenite matični broj, lozinka za prijavu se menja na novi matični broj, i ako ju je klijent ranije promenio.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="newLoginPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Nova lozinka (opciono)
                </label>
                <div className="relative mt-1">
                  <input
                    id="newLoginPassword"
                    type={showPassword ? 'text' : 'password'}
                    name="newLoginPassword"
                    autoComplete="new-password"
                    value={formik.values.newLoginPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(shown => !shown)}
                    aria-label={showPassword ? 'Sakrij lozinku' : 'Prikaži lozinku'}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {formik.touched.newLoginPassword && formik.errors.newLoginPassword && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.newLoginPassword}</div>
                )}
              </div>
            </FormSection>
          )}

          <FormSection title="Adresa">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grad
              </label>
              <input
                type="text"
                name="city"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.city && formik.errors.city && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.city}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresa
              </label>
              <input
                type="text"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.address && formik.errors.address && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.address}</div>
              )}
            </div>
          </FormSection>

          <FormSection title="Kontakt Informacije">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kontakt Osoba
              </label>
              <input
                type="text"
                name="contactPerson"
                value={formik.values.contactPerson}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.contactPerson && formik.errors.contactPerson && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.contactPerson}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kontakt Telefon
              </label>
              <input
                type="text"
                name="contactNumber"
                value={formik.values.contactNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.contactNumber && formik.errors.contactNumber && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.contactNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
              )}
            </div>
          </FormSection>

          <FormSection title="Dodatne Informacije">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valuta Plaćanja
              </label>
              <input
                type="text"
                name="paymentCurrency"
                value={formik.values.paymentCurrency}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.paymentCurrency && formik.errors.paymentCurrency && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.paymentCurrency}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="isActive"
                    value="true"
                    checked={formik.values.isActive === 'true'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span>Aktivan</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="isActive"
                    value="false"
                    checked={formik.values.isActive === 'false'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span>Neaktivan</span>
                </label>
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Komentar
              </label>
              <textarea
                name="comment"
                rows="4"
                value={formik.values.comment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </FormSection>

          <div className="flex justify-end space-x-4 pt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/clients')}
            >
              Odustani
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting}
            >
              Sačuvaj Izmene
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClient;

